import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Download, Eye, Paperclip } from "lucide-react";
import { useState } from "react";

import { triggerDownload } from "@/api/attachments";
import { fetchTestAttachmentBlob } from "@/api/test-attachments";
import { Spinner } from "@/components/Spinner";
import { AttachmentViewerModal } from "./AttachmentViewerModal";

type Cached = { blob: Blob; mimeType: string; fileName: string };
type Mode = "real" | "test";

function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = /filename\*=([^;]+)/i.exec(disposition);
  if (star) {
    const v = star[1].trim();
    const idx = v.indexOf("''");
    const raw = idx >= 0 ? v.slice(idx + 2) : v;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

function useAttachmentBlob(
  tabId: string,
  grievanceId: number,
  mode: Mode,
): UseQueryResult<Cached> {
  return useQuery({
    queryKey: ["attachment", mode, tabId, grievanceId],
    queryFn: async (): Promise<Cached> => {
      if (mode === "test") {
        return fetchTestAttachmentBlob(tabId, grievanceId);
      }
      const res = await fetch(
        `/api/admin/tabs/${tabId}/attachments/${grievanceId}?disposition=inline`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Attachment download failed (${res.status})`);
      const mimeType = res.headers.get("content-type") ?? "application/octet-stream";
      const fileName =
        parseFilename(res.headers.get("content-disposition")) ?? `attachment-${grievanceId}`;
      const blob = await res.blob();
      return { blob, mimeType, fileName };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function AttachmentPanel({
  tabId,
  grievanceId,
  mode = "real",
}: {
  tabId: string;
  grievanceId: number;
  mode?: Mode;
}) {
  const [viewerOpen, setViewerOpen] = useState(false);

  const q = useAttachmentBlob(tabId, grievanceId, mode);

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold inline-flex items-center gap-2">
          <Paperclip size={14} /> Student's Attachment
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={!q.data}
            onClick={() => q.data && triggerDownload(q.data.blob, q.data.fileName)}
          >
            <Download size={12} /> Download
          </button>
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={!q.data}
            onClick={() => setViewerOpen(true)}
          >
            <Eye size={12} /> View
          </button>
        </div>
      </div>
      {q.isLoading ? (
        <Spinner label="Loading attachment…" />
      ) : q.isError ? (
        <div className="alert-warning text-xs">
          Attachment was recorded, but could not be downloaded.
          {q.error instanceof Error ? ` ${q.error.message}` : ""}{" "}
          <button
            type="button"
            onClick={() => q.refetch()}
            className="ml-2 underline"
          >
            Retry
          </button>
        </div>
      ) : q.data ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
          {q.data.fileName}
          <span className="ml-2 text-slate-400">({q.data.mimeType})</span>
        </p>
      ) : null}

      <AttachmentViewerModal
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        blob={q.data?.blob ?? null}
        mimeType={q.data?.mimeType ?? "application/octet-stream"}
        fileName={q.data?.fileName ?? ""}
        tabId={tabId}
        grievanceId={grievanceId}
        mode={mode}
      />
    </section>
  );
}

/**
 * Compact one-line variant of AttachmentPanel meant to sit inline with a
 * section heading (e.g. next to "Student's Ticket"). Same blob-fetch under
 * the hood, but only renders the "Attachment: [View] [Download]" affordance.
 * View still opens the shared modal; on error the buttons stay disabled and
 * a tiny error hint appears below.
 */
export function AttachmentInline({
  tabId,
  grievanceId,
  mode = "real",
}: {
  tabId: string;
  grievanceId: number;
  mode?: Mode;
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const q = useAttachmentBlob(tabId, grievanceId, mode);

  return (
    <>
      <div className="inline-flex items-center gap-2 text-xs">
        <span className="text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
          <Paperclip size={12} /> Attachment:
        </span>
        <button
          type="button"
          className="btn-secondary text-xs px-2 py-1"
          disabled={!q.data}
          onClick={() => setViewerOpen(true)}
          title={q.isError ? "Attachment failed to load" : q.data?.fileName}
        >
          <Eye size={12} /> View
        </button>
        <button
          type="button"
          className="btn-secondary text-xs px-2 py-1"
          disabled={!q.data}
          onClick={() => q.data && triggerDownload(q.data.blob, q.data.fileName)}
        >
          <Download size={12} /> Download
        </button>
      </div>

      <AttachmentViewerModal
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        blob={q.data?.blob ?? null}
        mimeType={q.data?.mimeType ?? "application/octet-stream"}
        fileName={q.data?.fileName ?? ""}
        tabId={tabId}
        grievanceId={grievanceId}
        mode={mode}
      />
    </>
  );
}
